import { useCallback, useEffect } from "react";
import { RoadCarrier, CreateRoadCarrier, UpdateRoadCarrier } from "../types/models/RoadCarrier.d";
import { UpdateEntityRoadCarrier } from "../types/models/RoadCarrier";
import { useGetRoadCarrierListQuery, useInsertRoadCarrierMutation, useUpdateRoadCarrierMutation, useDeleteRoadCarrierMutation, } from "../apis/RoadCarrier.api";
import { useDeleteDialog, useDescriptionAlert } from '../../share';
import { useNavigate } from "react-router";
import { t } from "@lingui/core/macro";

export default function useRoadCarrier(page: number, pageSize: number) {

    const navigate = useNavigate()
    const { openDialog, closeDialog } = useDeleteDialog();

    const { openSuccessAlert, openFailAlert } = useDescriptionAlert()

    const [insertRoadCarrierMutation, { 
        isLoading: insertRoadCarrierLoading, 
        isSuccess: insertRoadCarrierIsSuccess,
        isError: insertRoadCarrierIsError,
        error: insertRoadCarrierError,
    }] = useInsertRoadCarrierMutation()
    const [updateRoadCarrierMutation, { 
        isLoading: updateRoadCarrierLoading,
        isSuccess: updateRoadCarrierIsSuccess,
        isError: updateRoadCarrierIsError,
        error: updateRoadCarrierError,
    }] = useUpdateRoadCarrierMutation()
    const [deleteRoadCarrierMutation, { 
        isLoading: deleteRoadCarrierLoading,
        isSuccess: deleteRoadCarrierIsSuccess,
        isError: deleteRoadCarrierIsError,
        error: deleteRoadCarrierError,
    }] = useDeleteRoadCarrierMutation()

    const loading =
        insertRoadCarrierLoading ||
        updateRoadCarrierLoading ||
        deleteRoadCarrierLoading ||
        false

    const insertRoadCarrier = useCallback(async (roadCarrier: CreateRoadCarrier, redirectPath: string|undefined) => {
        try {
            const data = await insertRoadCarrierMutation(roadCarrier).unwrap()
            if (data) {
                if (redirectPath) {
                    if (redirectPath !== '.') {
                        navigate(redirectPath, {relative: 'path'})
                    }
                } else {
                    navigate(`../${data.id}`, {relative: 'path'})
                }
                return data;
            }
        } catch (e) {
            console.log("add error", e)
            openFailAlert(t`Add error: ` + e.toString())
        }
    }, [insertRoadCarrierMutation, navigate])
    
    useEffect(() => {
        if (insertRoadCarrierIsSuccess) {
            openSuccessAlert('Road Carrier created')
        } else if (insertRoadCarrierIsError) {
            console.log("add error", insertRoadCarrierError)
            openFailAlert(t`Road Carrier add error: ` + insertRoadCarrierError?.data?.message)
        }
    }, [
        insertRoadCarrierIsSuccess,
        insertRoadCarrierIsError,
        insertRoadCarrierError
    ]);

    const updateRoadCarrier = useCallback(async (roadCarrier: UpdateEntityRoadCarrier, redirectPath: string|undefined) => {
        try {
            const data = await updateRoadCarrierMutation(roadCarrier).unwrap();
            if (data != null) {
                //if update success
                if (redirectPath) {
                    if (redirectPath !== '.') {
                        navigate(redirectPath, {relative: 'path'})
                    }
                } else {
                    navigate('..', {relative: 'path'})
                }
                return data;
            }
        } catch (e) {
            console.log("update error", e)
            openFailAlert(t`Update error: ` + e.toString())
        }
    }, [updateRoadCarrierMutation, navigate])
    
    useEffect(() => {
        if (updateRoadCarrierIsSuccess) {
            openSuccessAlert(t`Road Carrier updated`)
        } else if (updateRoadCarrierIsError) {
            console.log("update error", updateRoadCarrierError)
            openFailAlert(t`Road Carrier update error: ` + updateRoadCarrierError?.data?.message)
        }
    }, [
        updateRoadCarrierIsSuccess,
        updateRoadCarrierIsError,
        updateRoadCarrierError
    ]);

    const deleteRoadCarrier = useCallback((roadCarrier: RoadCarrier, redirectPath: string|undefined) => {
        openDialog(async () => {
            roadCarrier = {
                ...roadCarrier,
            }
            try {
                const { data } = await deleteRoadCarrierMutation(roadCarrier);
                if (data != null) {
                    if (redirectPath) {
                        navigate(redirectPath, {relative: 'path'})
                    }
                }
            } catch (e) {
                console.log("delete error", e)
                openFailAlert(t`Delete error: ` + e.toString())
            }
            closeDialog()
        });
    }, [deleteRoadCarrierMutation, closeDialog, openDialog])
    
    useEffect(() => {
        if (deleteRoadCarrierIsSuccess) {
            openSuccessAlert(t`Road Carrier deleted`)
        } else if (deleteRoadCarrierIsError) {
            console.log("delete error", deleteRoadCarrierError)
            openFailAlert(t`Road Carrier delete error: ` + deleteRoadCarrierError?.data?.message)
        }
    }, [
        deleteRoadCarrierIsSuccess,
        deleteRoadCarrierIsError,
        deleteRoadCarrierError
    ]);

    return {
        loading,
        insertRoadCarrier,
        updateRoadCarrier,
        deleteRoadCarrier
    }
}
